import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  InputAdornment,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { useNavigate } from 'react-router-dom';
import { fetchXeroContacts, fmtXeroMoney, type XeroContactRow } from '../../services/xeroApi';
import { XeroHero } from '../../components/xero/xeroShared';

const XeroCustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<XeroContactRow[]>([]);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchXeroContacts(query || undefined);
      setContacts(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <XeroHero compact title="Xero · Customers" subtitle="Customers with AR balances — jump to their unpaid invoices." />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Stack direction="row" gap={2} sx={{ mb: 2 }}>
        <TextField
          label="Search customers"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setQuery(search)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />
        <Button variant="contained" onClick={() => setQuery(search)}>
          Search
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell align="right">AR outstanding</TableCell>
              <TableCell align="right">AR overdue</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" py={4} textAlign="center">
                    No customers found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {contacts.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>
                  <Typography fontWeight={600}>{c.name}</Typography>
                </TableCell>
                <TableCell>{c.email || '—'}</TableCell>
                <TableCell>{c.phone || '—'}</TableCell>
                <TableCell align="right">{fmtXeroMoney(c.arOutstanding)}</TableCell>
                <TableCell align="right">
                  <Typography color={c.arOverdue > 0 ? 'error.main' : 'text.primary'} fontWeight={c.arOverdue > 0 ? 700 : 400}>
                    {fmtXeroMoney(c.arOverdue)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    startIcon={<ReceiptLongOutlinedIcon />}
                    onClick={() =>
                      navigate(`/xero/invoices?filter=unpaid&search=${encodeURIComponent(c.name)}&contactId=${c.id}`)
                    }
                  >
                    Invoices
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Box>
  );
};

export default XeroCustomersPage;
